/**
 * Hook: Fetch and manage project sources (multi-source support).
 *
 * Provides a React Query-backed list of project sources with automatic
 * polling when any source has 'profiling' status.
 */
import { useQuery, useQueryClient, useMutation } from 'react-query';
import {
  listProjectSources,
  addFileSources,
  addConnectorSource,
  removeProjectSource,
} from '../../../services/projectSourceApi';
import type { ProjectSource, AddConnectorSourceRequest } from '../../../services/projectSourceApi';

const QUERY_KEY = 'projectSources';

export function useProjectSources(projectId: string | undefined, batchId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery(
    [QUERY_KEY, projectId, batchId],
    () => listProjectSources(projectId!, batchId),
    {
      enabled: !!projectId,
      staleTime: 10_000,
      // Poll every 5s while any source is still profiling
      refetchInterval: (data) => {
        if (!data) return false;
        const hasProfiling = data.some((s: ProjectSource) => s.status === 'profiling' || s.status === 'pending');
        return hasProfiling ? 5_000 : false;
      },
    },
  );

  const addFiles = useMutation(
    (files: File[]) => addFileSources(projectId!, files),
    {
      onSuccess: () => queryClient.invalidateQueries([QUERY_KEY, projectId]),
    },
  );

  const addConnector = useMutation(
    (request: AddConnectorSourceRequest) => addConnectorSource(projectId!, request),
    {
      onSuccess: () => queryClient.invalidateQueries([QUERY_KEY, projectId]),
    },
  );

  const remove = useMutation(
    (sourceId: string) => removeProjectSource(projectId!, sourceId),
    {
      onSuccess: () => queryClient.invalidateQueries([QUERY_KEY, projectId]),
    },
  );

  return {
    sources: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    addFiles,
    addConnector,
    remove,
  };
}
